import { Box, Flex, useMultiStyleConfig, VStack } from '@chakra-ui/react';
import React from 'react';

import Button from '../Button';
import Divider, { DividerColorScheme } from '../Divider';
import ImageHandler from '../ImageHandler';
import { isStaticImageDef, isVideoDef } from '../Utils';

interface BannerProps {
  title: string;
  primaryAction?: {
    label: string;
    href: string;
  };
  secondaryAction?: {
    label: string;
    href: string;
  };
  darken?: boolean;
  media: StaticImageDef | VideoDef | React.ReactElement;
}

interface MediaHandlerProps {
  media: StaticImageDef | VideoDef | React.ReactElement;
}

const MediaHandler: React.FC<MediaHandlerProps> = ({ media }) => {
  if (isStaticImageDef(media)) {
    return <ImageHandler image={media} />;
  } else if (isVideoDef(media)) {
    // TODO Find correct way to tack on a11y alt text to a video - tooltip role on enclosing div/box?
    return (
      <video autoPlay muted loop playsInline poster={media.poster}>
        <source src={media.src} type={media.format} />
        Your browser does not support HTML5 video.
      </video>
    );
  }
  return media;
};

const Banner: React.FC<BannerProps> = ({
  title,
  primaryAction,
  secondaryAction,
  darken,
  media,
  children,
}) => {
  const Styles = useMultiStyleConfig('Banner', {});
  return (
    <Flex sx={Styles.container}>
      <VStack sx={Styles.overlay}>
        <Divider colorScheme={DividerColorScheme.BLUE} as="h6">
          {title}
        </Divider>
        <Box sx={Styles.content}>{children}</Box>
        <VStack sx={Styles.actions}>
          {primaryAction && (
            <Button
              href={primaryAction.href}
              colorScheme="blue"
              variant="outline"
              uppercase
              width="full"
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              href={secondaryAction.href}
              colorScheme="red"
              variant="solid"
              uppercase
              width="full"
            >
              {secondaryAction.label}
            </Button>
          )}
        </VStack>
      </VStack>

      <Box sx={Styles.media}>
        {darken && <Box sx={Styles.mediaDarken} />}
        <MediaHandler media={media} />
      </Box>
    </Flex>
  );
};

export default Banner;
